import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import {
  DictationCleanupRequest,
  DictationCleanupResponse,
} from '@chiroup/core/types/DictationCleanup.type';

export type DeepgramTokenResponse = {
  accessToken: string;
  expiresIn: number;
};

const deepgramService = () => {
  /**
   * Fetch a short-lived Deepgram token so the browser can open a live
   * transcription WebSocket directly to Deepgram. The long-lived project key
   * stays server-side (see backend deepgramTranscribe.ts).
   */
  const getToken = async (clinicId: number): Promise<DeepgramTokenResponse> => {
    return ChiroUpAPI.post('api', `/encounters/${clinicId}/voice-to-chart/deepgram-token`, {});
  };

  /**
   * Ask the server-side GPT cleanup pass for a structured patch resolving spoken
   * self-corrections in a finalized dictation utterance. Used by global dictation
   * only (never the Voice-to-Chart note path). Callers should fall back to the
   * already-committed deterministic text if this rejects/times out.
   */
  const cleanup = async (
    clinicId: number,
    request: DictationCleanupRequest
  ): Promise<DictationCleanupResponse> => {
    return ChiroUpAPI.post('api', `/encounters/${clinicId}/voice-to-chart/dictation-cleanup`, {
      body: request,
    });
  };

  /**
   * Report a completed dictation session's elapsed seconds so the time counts
   * toward the clinic's Voice-to-Chart usage/billing. Dictation streams live to
   * Deepgram and produces no upload, so this is the only usage signal. Callers
   * fire-and-forget — a dropped report just isn't billed, never blocking the UI.
   */
  const recordDictationUsage = async (
    clinicId: number,
    seconds: number
  ): Promise<{ voiceToChartMinutes: number | null; minutesAdded: number }> => {
    return ChiroUpAPI.post('api', `/encounters/${clinicId}/voice-to-chart/dictation-usage`, {
      body: { seconds },
    });
  };

  return { getToken, cleanup, recordDictationUsage };
};

export default deepgramService();
